import logger from '@/lib/logging';

import { SELF_ASSESSMENT_PUBLIC_PATH } from '../self-assessment-public-path';

/** Hyros tracking host for the public posture funnel. Also allowed by the server CSP. */
export const HYROS_FUNNEL_ORIGIN = 'https://t.conorharristraining.com';

export const HYROS_FUNNEL_SCRIPT_PATH =
  '/v1/6d138094e178932efb6a276dbfd13fb7c97e879d5af95f211477f0914a4c8e54';

export const HYROS_FUNNEL_TAG = '!posture';

export const HYROS_FUNNEL_SCRIPT_ID = 'hyros-funnel';

/** Hyros universal script listens for this and creates the lead. */
export const HYROS_EMAIL_EVENT = 'hyros_email';

const reportedEmails = new Set<string>();
const reportedEvents = new Set<string>();

/** Steps worth keeping for funnel analysis and ad optimization. */
export const HYROS_FUNNEL_EVENTS = {
  funnelOpened: 'funnel_opened',
  assessmentStarted: 'assessment_started',
  photoFront: 'photo_front',
  photoSide: 'photo_side',
  photosReady: 'photos_ready',
  analysisStarted: 'analysis_started',
  resultsReady: 'results_ready',
  analysisFailed: 'analysis_failed',
  applicationOpened: 'application_opened',
  surveyOpened: 'survey_opened',
  applicationSubmitted: 'application_submitted',
  pdfFormOpened: 'pdf_form_opened',
  reportDownloaded: 'report_downloaded',
  calendarOpened: 'calendar_opened',
  callBooked: 'call_booked',
} as const;

type HyrosLeadApi = {
  addLeadTag: (tag: string) => void;
  addLeadStage: (stage: string) => void;
};

type HyrosWindow = Window & { hrs?: Partial<HyrosLeadApi> };

export function isHyrosFunnelPath(pathname: string): boolean {
  const trimmed =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return trimmed === SELF_ASSESSMENT_PUBLIC_PATH;
}

/** Same query the Hyros head snippet builds: `encodeURI(document.URL)`. */
export function buildHyrosFunnelScriptSrc(documentUrl: string): string {
  return (
    `${HYROS_FUNNEL_ORIGIN}${HYROS_FUNNEL_SCRIPT_PATH}` +
    `?tag=${HYROS_FUNNEL_TAG}&ref_url=${encodeURI(documentUrl)}`
  );
}

/**
 * Append the Hyros tracker to `document.head` on the public posture funnel.
 * Direct visits also get the inline copy in `index.html` so the pixel runs
 * before the app bundle. This covers client-side navigation onto the funnel.
 */
export function installHyrosFunnelScript(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (!isHyrosFunnelPath(window.location.pathname)) return;
  if (document.getElementById(HYROS_FUNNEL_SCRIPT_ID)) return;

  const hyrosHead = document.head;
  const hyrosScript = document.createElement('script');
  hyrosScript.id = HYROS_FUNNEL_SCRIPT_ID;
  hyrosScript.type = 'text/javascript';
  hyrosScript.src = buildHyrosFunnelScriptSrc(document.URL);
  hyrosHead.appendChild(hyrosScript);
}

function hyrosLeadApi(): HyrosLeadApi | null {
  const hrs = (window as HyrosWindow).hrs;
  if (!hrs || typeof hrs.addLeadTag !== 'function' || typeof hrs.addLeadStage !== 'function') {
    return null;
  }
  return hrs as HyrosLeadApi;
}

function whenHyrosReady(run: () => void): void {
  installHyrosFunnelScript();
  if ((window as HyrosWindow).hrs) {
    run();
    return;
  }
  const script = document.getElementById(HYROS_FUNNEL_SCRIPT_ID);
  script?.addEventListener('load', () => run(), { once: true });
}

function sanitizeHyrosEventName(name: string): string {
  return name.replace(/[^a-zA-Z0-9_]/g, '_').replace(/_+/g, '_').slice(0, 64);
}

/**
 * One Hyros lead tag and stage per event name. Tags accumulate for analysis.
 * `application_submitted` is the form-submit conversion used to optimize ads.
 */
export function trackHyrosFunnelEvent(name: string): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (!isHyrosFunnelPath(window.location.pathname)) return;

  const eventName = sanitizeHyrosEventName(name.trim());
  if (!eventName || reportedEvents.has(eventName)) return;
  reportedEvents.add(eventName);

  whenHyrosReady(() => {
    const hrs = hyrosLeadApi();
    if (!hrs) {
      reportedEvents.delete(eventName);
      return;
    }
    hrs.addLeadStage(eventName);
    hrs.addLeadTag(eventName);
    logger.info('[Hyros] funnel event', { event: eventName });
  });
}

export function trackHyrosFunnelSurveyStep(step: string): void {
  const safe = sanitizeHyrosEventName(step.trim());
  if (!safe) return;
  trackHyrosFunnelEvent(`survey_step_${safe}`.slice(0, 64));
}

/**
 * The posture application is a cross-origin GoHighLevel survey iframe.
 * Hyros only reads emails in this document, so a survey submit never becomes
 * a lead unless we forward the address the survey already sent us.
 */
export function reportHyrosFunnelEmail(email: string): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (!isHyrosFunnelPath(window.location.pathname)) return;

  const trimmed = email.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return;
  if (reportedEmails.has(trimmed)) return;
  reportedEmails.add(trimmed);

  whenHyrosReady(() => {
    window.dispatchEvent(
      new CustomEvent(HYROS_EMAIL_EVENT, { detail: { email: trimmed } }),
    );
    logger.info('[Hyros] reported funnel lead email');
  });
}

/** Test helper. A real page load starts with an empty set. */
export function clearReportedHyrosFunnelEmails(): void {
  reportedEmails.clear();
  reportedEvents.clear();
}
